/**
 * /change-password — authenticated, owner-performed password rotation.
 *
 * Two arrival paths:
 *  1. FORCED (099 must_change_password): the auth gate bounces every
 *     /app/* load here while the flag is set — an admin provisioned the
 *     account with a temporary password (Add people without invite
 *     email) and the joiner must pick their own before using the app
 *     (2026-07-11 UX audit 🟡 #12).
 *  2. Voluntary: any signed-in user rotating their credential.
 *
 * POST /api/v1/auth/change-password requires the CURRENT password as
 * proof of ownership; on success the backend revokes every other
 * session, re-issues cookies for this client, and clears the flag —
 * then we refresh /me and honour ?next=.
 */

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi } from "@/lib/api";
import { useAuth, safeNext } from "@/lib/auth";
import { passwordSchema } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";
import { sessionLanding } from "@/lib/auth/auth-gate";

const changePasswordSearchSchema = z.object({
  next: z.string().optional().catch(undefined),
});

const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, "Your current password is required"),
    new_password: passwordSchema,
    confirm_password: z.string().min(1, "Repeat the new password"),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  })
  .refine((v) => v.new_password !== v.current_password, {
    message: "The new password must be different",
    path: ["new_password"],
  });

type ChangePasswordValues = z.infer<typeof changePasswordSchema>;

const FIELDS = ["current_password", "new_password"] as const;

export const Route = createFileRoute("/_auth/change-password")({
  validateSearch: changePasswordSearchSchema,
  component: ChangePasswordPage,
});

function ChangePasswordPage() {
  const navigate = useNavigate();
  const { next: nextRaw } = Route.useSearch();
  const { user, refresh } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const nextPath = safeNext(nextRaw);
  const forced = user?.must_change_password === true;

  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  async function onSubmit(values: ChangePasswordValues) {
    setSubmitting(true);
    try {
      await authApi.changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      const me = await refresh();
      toast.success("Password changed.");
      // Round 2 T-H1: the temporary password is changed BEFORE 2FA is set
      // up, so a mandatory enrolment (enrolment-only session) comes next;
      // otherwise the workspace / ?next=.
      const route = sessionLanding(me, nextPath);
      void navigate({ to: route.to, search: route.search as never, replace: true });
    } catch (err) {
      handleAuthError(err, form.setError, FIELDS);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="mb-1 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <KeyRound className="h-4 w-4" />
          </span>
          <CardTitle className="text-2xl">
            {forced ? "Choose Your Own Password" : "Change Password"}
          </CardTitle>
        </div>
        <CardDescription>
          {forced ? (
            <>
              Your account was set up with a temporary password shared by
              your admin. Pick your own before continuing — nobody else
              should know it.
              {user?.enrollment_required &&
                " Next, you'll set up two-factor authentication."}
            </>
          ) : (
            <>
              Changing your password signs out every other device and
              session for your whole account.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="current_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {forced ? "Temporary Password" : "Current Password"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="new_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    At least 12 characters, mixing 3 of: lowercase,
                    uppercase, digit, symbol.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirm_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Repeat New Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Change Password"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
